/**
 * Form Builder Types
 *
 * Comprehensive type definitions for all form builder components.
 * Supports Eligibility, Ranking, Branding, Journey, and Schema builders.
 */

import type {
  AttributeType,
  ComparisonOperator,
  EligibilityConfig,
  RankingConfig,
  NormalizationMethod,
  MissingValueStrategy,
  BrandingConfig,
  JourneyConfig,
  QuestionConfig,
} from "@tatvex/config";

// ============================================================================
// Common Types
// ============================================================================

/** View mode for editors */
export type ViewMode = "code" | "form";

/** Validation error for form fields */
export interface FieldError {
  readonly path: string;
  readonly message: string;
}

/** Form builder validation result */
export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: readonly FieldError[];
}

/** Form builder change handler */
export type OnChangeHandler<T> = (value: T) => void;

/** Form builder validation handler */
export type OnValidateHandler<T> = (value: T) => ValidationResult;

// ============================================================================
// Base Form Builder Props
// ============================================================================

/** Base props for all form builders */
export interface FormBuilderBaseProps<T> {
  /** Current value */
  readonly value: T;
  /** Change handler */
  readonly onChange: OnChangeHandler<T>;
  /** Optional validation handler */
  readonly onValidate?: OnValidateHandler<T>;
  /** Whether the form is read-only */
  readonly readOnly?: boolean;
  /** External validation errors */
  readonly errors?: readonly FieldError[];
  /** CSS class name */
  readonly className?: string;
}

// ============================================================================
// Eligibility Form Builder Types
// ============================================================================

/** Condition types for eligibility rules */
export type ConditionType = "comparison" | "and" | "or" | "not";

/** Comparison condition (leaf node) */
export interface ComparisonConditionValue {
  readonly field: string;
  readonly operator: ComparisonOperator;
  readonly value?: unknown | undefined;
  readonly valueRef?: string | undefined;
}

/** Recursive condition value */
export type ConditionValue =
  | (ComparisonConditionValue & { readonly type?: never })
  | { readonly type: "and"; readonly conditions: readonly ConditionValue[] }
  | { readonly type: "or"; readonly conditions: readonly ConditionValue[] }
  | { readonly type: "not"; readonly condition: ConditionValue };

/** Eligibility rule form value */
export interface EligibilityRuleValue {
  readonly id: string;
  readonly name: string;
  readonly description?: string | undefined;
  readonly conditions: readonly ConditionValue[];
  readonly reasonCode: string;
  readonly reasonMessage: string;
  readonly priority?: number | undefined;
}

/** Props for eligibility form builder */
export interface EligibilityFormBuilderProps extends FormBuilderBaseProps<EligibilityConfig> {
  /** Available attribute keys for condition fields */
  readonly availableFields?: readonly string[];
}

/** Props for condition builder sub-component */
export interface ConditionBuilderProps {
  readonly value: ConditionValue;
  readonly onChange: OnChangeHandler<ConditionValue>;
  readonly availableFields?: readonly string[];
  readonly depth?: number;
  readonly maxDepth?: number;
  readonly readOnly?: boolean;
}

/** Props for rule builder sub-component */
export interface RuleBuilderProps {
  readonly value: EligibilityRuleValue;
  readonly onChange: OnChangeHandler<EligibilityRuleValue>;
  readonly availableFields?: readonly string[];
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
}

// ============================================================================
// Ranking Form Builder Types
// ============================================================================

/** Ranking weight form value */
export interface RankingWeightValue {
  readonly attribute: string;
  readonly weight: number;
  readonly direction: "asc" | "desc";
  readonly normalization: NormalizationMethod;
  readonly missingValueStrategy: MissingValueStrategy;
}

/** Ranking strategy form value */
export interface RankingStrategyValue {
  readonly id: string;
  readonly name: string;
  readonly description?: string | undefined;
  readonly isDefault?: boolean | undefined;
  readonly weights: readonly RankingWeightValue[];
  readonly tiebreaker?: readonly string[] | undefined;
}

/** Props for ranking form builder */
export interface RankingFormBuilderProps extends FormBuilderBaseProps<RankingConfig> {
  /** Available attribute keys for weights */
  readonly availableAttributes?: readonly string[];
}

/** Props for weight builder sub-component */
export interface WeightBuilderProps {
  readonly value: RankingWeightValue;
  readonly onChange: OnChangeHandler<RankingWeightValue>;
  readonly availableAttributes?: readonly string[];
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
}

/** Props for strategy builder sub-component */
export interface StrategyBuilderProps {
  readonly value: RankingStrategyValue;
  readonly onChange: OnChangeHandler<RankingStrategyValue>;
  readonly availableAttributes?: readonly string[];
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
}

// ============================================================================
// Branding Form Builder Types
// ============================================================================

/** Branding colors form value */
export interface BrandingColorsValue {
  readonly primary: string;
  readonly accent: string;
  readonly background: string;
  readonly surface: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly error?: string;
  readonly success?: string;
  readonly warning?: string;
}

/** Branding fonts form value */
export interface BrandingFontsValue {
  readonly heading?: string;
  readonly body?: string;
}

/** Branding form value */
export interface BrandingFormValue {
  readonly verticalKey?: string;
  readonly version: number;
  readonly logoUrl?: string;
  readonly faviconUrl?: string;
  readonly colors: BrandingColorsValue;
  readonly fonts?: BrandingFontsValue;
  readonly customCss?: string;
}

/** Props for branding form builder */
export interface BrandingFormBuilderProps extends FormBuilderBaseProps<BrandingConfig> {
  /** Whether to show custom CSS editor */
  readonly showCustomCss?: boolean;
}

/** Props for color picker sub-component */
export interface ColorPickerProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly readOnly?: boolean;
  readonly required?: boolean;
}

// ============================================================================
// Journey Form Builder Types
// ============================================================================

/** Journey step types */
export type JourneyStepType = "form" | "results" | "details" | "click_out";

/** Question option form value */
export interface QuestionOptionValue {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
  readonly icon?: string;
}

/** Form field form value */
export interface FormFieldValue {
  readonly key: string;
  readonly question: QuestionConfig;
  readonly visibilityCondition?: ComparisonConditionValue;
}

/** Conditional next step form value */
export interface ConditionalNextStepValue {
  readonly stepId: string;
  readonly condition?: ComparisonConditionValue;
}

/** Journey step form value */
export interface JourneyStepValue {
  readonly id: string;
  readonly type: JourneyStepType;
  readonly config: unknown;
  readonly nextSteps?: readonly ConditionalNextStepValue[];
}

/** Props for journey form builder */
export interface JourneyFormBuilderProps extends FormBuilderBaseProps<JourneyConfig> {
  /** Available step IDs for next step references */
  readonly availableStepIds?: readonly string[];
}

/** Props for step builder sub-component */
export interface StepBuilderProps {
  readonly value: JourneyStepValue;
  readonly onChange: OnChangeHandler<JourneyStepValue>;
  readonly availableStepIds?: readonly string[];
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
}

/** Props for form field builder sub-component */
export interface FormFieldBuilderProps {
  readonly value: FormFieldValue;
  readonly onChange: OnChangeHandler<FormFieldValue>;
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
}

// ============================================================================
// Schema Form Builder Types
// ============================================================================

/** Attribute definition form value */
export interface AttributeDefinitionValue {
  readonly key: string;
  readonly type: AttributeType;
  readonly label: string;
  readonly description?: string;
  readonly required: boolean;
  readonly constraints?: Record<string, unknown>;
  readonly display?: Record<string, unknown>;
  readonly defaultValue?: unknown;
  readonly computed?: boolean;
  readonly computeExpression?: string;
}

/** Derived attribute form value */
export interface DerivedAttributeValue extends AttributeDefinitionValue {
  readonly computed: true;
  readonly computeExpression: string;
  readonly dependencies: readonly string[];
}

/** Schema form value */
export interface SchemaFormValue {
  readonly verticalKey: string;
  readonly version: number;
  readonly displayName?: string;
  readonly description?: string;
  readonly userInputs: readonly AttributeDefinitionValue[];
  readonly productAttributes: readonly AttributeDefinitionValue[];
  readonly derivedAttributes?: readonly DerivedAttributeValue[];
}

/** Props for schema form builder */
export interface SchemaFormBuilderProps extends FormBuilderBaseProps<SchemaFormValue> {
  /** Whether to show derived attributes section */
  readonly showDerivedAttributes?: boolean;
}

/** Props for attribute builder sub-component */
export interface AttributeBuilderProps {
  readonly value: AttributeDefinitionValue;
  readonly onChange: OnChangeHandler<AttributeDefinitionValue>;
  readonly readOnly?: boolean;
  readonly onDelete?: () => void;
  readonly existingKeys?: readonly string[];
}

// ============================================================================
// View Toggle Types
// ============================================================================

/** Props for view mode toggle */
export interface ViewModeToggleProps {
  readonly mode: ViewMode;
  readonly onChange: (mode: ViewMode) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

/** Props for unified config editor with toggle */
export interface ConfigEditorProps<T> {
  readonly value: T;
  readonly onChange: OnChangeHandler<T>;
  readonly onValidate?: OnValidateHandler<T>;
  readonly configType: "eligibility" | "ranking" | "branding" | "journey" | "schema";
  readonly defaultMode?: ViewMode;
  readonly readOnly?: boolean;
  readonly className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Check if a condition is a comparison condition */
export function isComparisonCondition(
  condition: ConditionValue
): condition is ComparisonConditionValue & { type?: never } {
  return !("type" in condition) || condition.type === undefined;
}

/** Check if a condition is an AND condition */
export function isAndCondition(
  condition: ConditionValue
): condition is { type: "and"; conditions: readonly ConditionValue[] } {
  return "type" in condition && condition.type === "and";
}

/** Check if a condition is an OR condition */
export function isOrCondition(
  condition: ConditionValue
): condition is { type: "or"; conditions: readonly ConditionValue[] } {
  return "type" in condition && condition.type === "or";
}

/** Check if a condition is a NOT condition */
export function isNotCondition(
  condition: ConditionValue
): condition is { type: "not"; condition: ConditionValue } {
  return "type" in condition && condition.type === "not";
}

/** Create an empty comparison condition */
export function createEmptyComparisonCondition(): ComparisonConditionValue {
  return {
    field: "",
    operator: "eq",
    value: "",
  };
}

/** Create an empty eligibility rule */
export function createEmptyEligibilityRule(): EligibilityRuleValue {
  return {
    id: "",
    name: "",
    conditions: [createEmptyComparisonCondition()],
    reasonCode: "",
    reasonMessage: "",
  };
}

/** Create an empty ranking weight */
export function createEmptyRankingWeight(): RankingWeightValue {
  return {
    attribute: "",
    weight: 0,
    direction: "desc",
    normalization: "min-max",
    missingValueStrategy: "worst",
  };
}

/** Create an empty ranking strategy */
export function createEmptyRankingStrategy(): RankingStrategyValue {
  return {
    id: "",
    name: "",
    weights: [createEmptyRankingWeight()],
  };
}

/** Create empty branding colors */
export function createEmptyBrandingColors(): BrandingColorsValue {
  return {
    primary: "#3B82F6",
    accent: "#8B5CF6",
    background: "#FFFFFF",
    surface: "#F3F4F6",
    textPrimary: "#111827",
    textSecondary: "#6B7280",
  };
}

/** Create an empty journey step */
export function createEmptyJourneyStep(type: JourneyStepType): JourneyStepValue {
  const baseConfig = {
    form: {
      type: "form" as const,
      title: "",
      fields: [],
      submitLabel: "Continue",
    },
    results: {
      type: "results" as const,
      title: "Results",
      showStrategySelector: true,
      defaultStrategy: "",
      resultsPerPage: 10,
      showRankExplanation: true,
    },
    details: {
      type: "details" as const,
      sections: [],
    },
    click_out: {
      type: "click_out" as const,
      trackingEnabled: true,
    },
  };

  return {
    id: "",
    type,
    config: baseConfig[type],
  };
}

/** Create an empty attribute definition */
export function createEmptyAttributeDefinition(): AttributeDefinitionValue {
  return {
    key: "",
    type: "string",
    label: "",
    required: false,
  };
}

/** Calculate total weight from weights array */
export function calculateTotalWeight(weights: readonly RankingWeightValue[]): number {
  return weights.reduce((sum, w) => sum + w.weight, 0);
}

/** Validate weight sum equals 1 */
export function validateWeightSum(weights: readonly RankingWeightValue[]): boolean {
  const total = calculateTotalWeight(weights);
  return Math.abs(total - 1) < 0.001;
}

/** Generate unique ID */
export function generateId(prefix: string): string {
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}_${random}`;
}

/** Validate hex color format */
export function isValidHexColor(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}

/** Validate camelCase format */
export function isValidCamelCase(value: string): boolean {
  return /^[a-z][a-zA-Z0-9]*$/.test(value);
}

/** Validate kebab-case format */
export function isValidKebabCase(value: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(value);
}

/** Validate SCREAMING_SNAKE_CASE format */
export function isValidScreamingSnakeCase(value: string): boolean {
  return /^[A-Z][A-Z0-9_]*$/.test(value);
}
